import {DatePicker, Form, Modal, TimePicker, Row, Col} from "antd";
import {useFormik} from "formik";
import moment from "moment";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import {useStore} from "../../../../hooks/useStore";
import locale from "antd/es/date-picker/locale/tr_TR";

const ShiftUpdateModal = ({courierName, id, visibility, date, startTime, endTime, onCancel}) => {
    const {t} = useTranslation();
    const store = useStore();
    const [confirmLoading, setConfirmLoading] = useState();

    const formik = useFormik({
        initialValues: {date, startTime, endTime},
        validationSchema: yup.object().shape({
            date: yup.string().required(t("validation.required", {ns: "common"})),
            startTime: yup.string().required(),
            endTime: yup.string().required()
        }),
        enableReinitialize: true,
        onSubmit: async values => {
            setConfirmLoading(true);
            try {
                const shiftStartTime = moment(values.date).set({
                    hour: values.startTime.format("HH"),
                    minute: values.startTime.format("mm")
                });
                const shiftEndTime = moment(values.date).set({
                    hour: values.endTime.format("HH"),
                    minute: values.endTime.format("mm")
                });

                if (shiftEndTime.diff(shiftStartTime) <= 0) {
                    shiftEndTime.add(1, "days");
                }
                const updateShiftResult = await store.shiftStore.updateShift({
                    id,
                    date: values.date,
                    shiftStartTime,
                    shiftEndTime
                });

                if (updateShiftResult?.success) {
                    const message = updateShiftResult.message;
                    store.appStore.showSuccessMessage({
                        content: message
                    });
                }
               
                onCancel(true);
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });

    const {values, errors, touched, handleSubmit, setFieldValue} = formik;

    const getDisabledDate = current => {
        let customDate = moment().format("DD.MM.YYYY");
        return current && current < moment(customDate, "DD.MM.YYYY");
    };

    return (
        <Modal
            title={courierName}
            visible={visibility}
            onCancel={() => {
                onCancel(false);
            }}
            onOk={handleSubmit}
            confirmLoading={confirmLoading}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form labelCol={{span: 24}} wrapperCol={{span: 24}}>
                <Row>
                    <Col span={24}>
                        <Form.Item
                            label={t("fields.date", {ns: "common"})}
                            validateStatus={errors.date && touched.date ? "error" : "success"}
                            help={errors.date && touched.date ? errors.date : null}
                        >
                            <DatePicker
                                value={values.date}
                                format={"DD.MM.YYYY"}
                                disabledDate={getDisabledDate}
                                onSelect={date => setFieldValue("date", date)}
                                locale={locale}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            label={t("fields.shiftStartTime", {ns: "common"})}
                            validateStatus={
                                errors.startTime && touched.startTime ? "error" : "success"
                            }
                            help={errors.startTime && touched.startTime ? errors.startTime : null}
                        >
                            <TimePicker
                                format={"HH:mm"}
                                value={values.startTime}
                                onChange={time => setFieldValue("startTime", time)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={t("fields.shiftEndTime", {ns: "common"})}
                            validateStatus={errors.endTime && touched.endTime ? "error" : "success"}
                            help={errors.endTime && touched.endTime ? errors.endTime : null}
                        >
                            <TimePicker
                                format={"HH:mm"}
                                value={values.endTime}
                                onChange={time => setFieldValue("endTime", time)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ShiftUpdateModal;
